import { useState, useEffect, useContext, useReducer } from 'react';
import { COSMOS_API, POST_ENDPOINT, GET_ENDPOINT } from '../api/API';
import {
  UserOrganizations,
  userDataReducer,
  UserUpdateAction,
  ORG_SHARED_DATA,
  init_newUserDataObj,
} from '../interfaces/UserInterfaces';
import './AddUser.css';
import PopUp from './PopUp';
import { AdminContext } from '../contexts/AdminContext';
import { OptionsType } from '../interfaces/ApplicationInterfaces';
import { AppEnvSetting, generateOptionType, isNullUndefEmpty } from '../ts/common';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import {
  ApplicationData,
  ClaimValue,
  Claims,
  ExternalEnumRecord,
  Organization,
  claimEnabledInContext,
  claimsListToSchema,
  makeExternalEnumRecord,
} from '../interfaces/APIInterface';
import Stack from '@mui/material/Stack';
import Grid from '@mui/material/Grid';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import ErrorIcon from '@mui/icons-material/Error';
import { useSnackbar } from 'notistack';
import MenuItem from '@mui/material/MenuItem';
import APPSETTINGS from '../settings.json';
import { ApplicationClaimField } from './ApplicationClaimField';
import { Alert } from '@mui/material';

const AddUser = () => {
  const [selectedApps, setSelectedApps] = useState<Array<OptionsType>>([]);
  const [defaultApp, setDefaultApp] = useState<OptionsType>();
  const [newUser, dispatch] = useReducer(userDataReducer, {
    ...init_newUserDataObj(),
  });
  const [setupInstructions, setSetupInstructions] = useState<boolean>(false);
  const [applications, setApplications] = useState<Array<OptionsType>>([]);
  const [msg, setMsg] = useState<string>('');
  const [showMsg, setShowMsg] = useState<boolean>(false);

  const [orgLevelClaims, setOrgLevelClaims] = useState<Array<Claims>>([]);
  const [appClaims, setAppClaims] = useState<Record<string, Array<Claims>>>({});
  const [validUserTypesByApp, setValidUserTypesByApp] = useState<Record<string, Array<string> | null>>({});
  const [validUserTypes, setValidUserTypes] = useState<Array<string> | null>(null);
  const [appExternalEnums, setAppExternalEnums] = useState < Record<string, Record<string, ExternalEnumRecord> > >({});
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  const {adminCredentials, getToken} = useContext(AdminContext);
  const [globalLevelClaims, setGlobalLevelClaims] = useState<Array<Claims>>([]);
  const [organizationToEdit, setOrganizationToEdit] = useState<string>(
    adminCredentials.organizationList?.length > 1 ? "" :
    adminCredentials.organizationId
  );
  const [organizations, setOrganizations] = useState<
    Array<{ id: string; name: string }>
  >([]);

  useEffect(() => {
    /* https://stackoverflow.com/a/60907638 */
    let isMounted = true;

      if (adminCredentials.isSuperAdmin || (adminCredentials.isOrganizationAdmin && adminCredentials.multiOrg)) {
      setOrganizations(adminCredentials.organizationList?.map(adminOrg => { return { id: adminOrg.id, name: adminOrg.name } }).sort((a, b) => (a.name <= b.name ? -1 : 1)));
      setOrganizationToEdit(adminCredentials.organizationList?.[0]?.id ?? "");
    } else {
            if (isMounted) {
        setOrganizations([{ id: adminCredentials.organizationId, name: '' }]);
        setOrganizationToEdit(adminCredentials.organizationId);
              }
            }

    COSMOS_API.GET(GET_ENDPOINT.GetConfiguration, getToken()).then(
      (res) => {
        if (isMounted) {
          if (res?.respBody?.claims) {
            setGlobalLevelClaims(res.respBody.claims);
        }
      }
    }
    );

    return () => {
      isMounted = false;
    };
  }, [adminCredentials]);

  useEffect(() => {
    if (!isNullUndefEmpty(organizationToEdit)) {
      updateOrgData();
    }
  }, [organizationToEdit]);

  async function updateOrgData() {
    let orgDataResp = await COSMOS_API.POST(
      { id: organizationToEdit },
      POST_ENDPOINT.GetOrganizationData,
      getToken()
    );

    if (orgDataResp.ok && orgDataResp.respBody !== undefined && orgDataResp.respBody !== null) {
      setOrgLevelClaims(orgDataResp.respBody.claims);
    }

    if (adminCredentials.isSuperAdmin || (adminCredentials.isOrganizationAdmin && adminCredentials.multiOrg)) {
      let apps: Array<OptionsType> = [];
      apps = Object.keys(adminCredentials.allApplications).reduce(function (
        result,
        appId
      ) {
        if ((orgDataResp.respBody.appList && orgDataResp.respBody.appList.includes(appId)) ||
            ((appId === APPSETTINGS.msalClientId) && adminCredentials.generalPermissions.manageAdmins) || (adminCredentials.allApplications[appId]?.isGlobal)){
          result.push(
            generateOptionType(adminCredentials.allApplications[appId]?.name, appId)
          );
        }

        return result;
      },
      apps);

      setApplications([...apps]);
    } else {
      let appsWithPermissionResp = await COSMOS_API.POST(
        { permission: 'CreateUser' },
        POST_ENDPOINT.GetApplicationsWithPermission,
        getToken()
      )
      
      if(appsWithPermissionResp.ok){
        // do we need to move this to happen with every org update and use the org level app list? I think so
        let appsWithPermission: Array<string> = appsWithPermissionResp.respBody.apps;
        const apps: Array<OptionsType> = appsWithPermission.map(
          (appPermitted: any) => {
            return generateOptionType(
              adminCredentials.allApplications[appPermitted]?.name,
              appPermitted
            );
          }
        );

        if ((!apps.some(appInfo => appInfo.value === APPSETTINGS.msalClientId)) &&
          adminCredentials.generalPermissions.manageAdmins) {
          apps.push(generateOptionType(adminCredentials.allApplications[APPSETTINGS.msalClientId]?.name, APPSETTINGS.msalClientId));
        }

        apps.sort((a, b) => (a.label <= b.label ? -1 : 1));
        setApplications(apps);
      }
    }

    let orgs: UserOrganizations = {};
    if (adminCredentials.isSuperAdmin || (adminCredentials.isOrganizationAdmin && adminCredentials.multiOrg)) {
      orgs = structuredClone(newUser.organizations);

      Object.keys(orgs).forEach((key) => {
        let orgString = JSON.stringify(orgs[key]);
        if (orgString === '{}') delete orgs[key];
        else if(Object.keys(orgs[key]).length === 1 && Object.keys(orgs[key])[0] === ORG_SHARED_DATA && JSON.stringify(orgs[key][ORG_SHARED_DATA]) === '{}') delete orgs[key];
      });
    }

    if (orgs[organizationToEdit] === undefined) {
      // If we want to consider allowing multiple organizations for add new user in the future:
      // orgs[organizationToEdit] = { [ORG_SHARED_DATA]: {} };
      orgs = {[organizationToEdit]: { [ORG_SHARED_DATA]: {} }};
    }
    dispatch({
      type: UserUpdateAction.ORGANIZATIONS,
      payload: { data: { ...orgs } },
    });
    setSelectedApps([]);
  }

  const handleChange = async (e: Array<OptionsType>) => {
    var newApps: Record<string, Record<string, string>> = newUser.organizations[
      organizationToEdit
    ]
      ? structuredClone(newUser.organizations[organizationToEdit])
      : { [ORG_SHARED_DATA]: {} };
    var newGlobalApps: Record<string, Record<string, string>> = newUser.applications ? structuredClone(newUser.applications) : {};

    setSelectedApps([...e]);

    if(e.length > 0 && (defaultApp === undefined || e.find((app) => defaultApp.value === app.value ) === undefined)){
      setDefaultApp(e[0]);
    }
    else {
      setDefaultApp(undefined);
    }

    if (Object.keys(newApps).length > 0) {
      Object.keys(newApps).forEach((app) => {
        if (
          app !== ORG_SHARED_DATA &&
          e.find((newApp) => newApp.value === app) === undefined
        ) {
          delete newApps[app];
        }
      });
    }

    if (Object.keys(newGlobalApps).length > 0) {
      Object.keys(newGlobalApps).forEach((app) => {
        if (
          app !== ORG_SHARED_DATA &&
          e.find((newGlobalApp) => newGlobalApp.value === app) === undefined
        ) {
          delete newGlobalApps[app];
        }
      });
    }

    var newAppClaims = structuredClone(appClaims);

    Object.keys(newAppClaims).forEach((currentApp) => {
      if (e.find((app) => app.value === currentApp) === undefined) {
        delete newAppClaims[currentApp];
      }
    });

    let validUserTypeAppInfo = { ...validUserTypesByApp };
    let validUserTypesInfo: Array<string> | null = null;

    for (const [index, selected] of e.entries()) {
      let isGlobalApp = adminCredentials.allApplications[selected.value]?.isGlobal ?? false;
      if ((newAppClaims[selected.value] === undefined) || (validUserTypeAppInfo[selected.value] === undefined)) {
        let appOrgTarget = isGlobalApp ? null : organizationToEdit;
        var appData: ApplicationData = (await COSMOS_API.POST(
          { id: selected.value },
          POST_ENDPOINT.GetApplicationData,
          getToken()
        )).respBody;
        newAppClaims[selected.value] = appData && appData.claims ? appData.claims : [];
        if (appData?.allowedUserTypes) {
          validUserTypeAppInfo = { ...validUserTypeAppInfo, [selected.value]: [...appData.allowedUserTypes] };
        } else {
          validUserTypeAppInfo = { ...validUserTypeAppInfo, [selected.value]: null };
        }
        if (appData?.claims?.some(c => !!c.externalEnumeration) && (!appExternalEnums.hasOwnProperty(appData.id))) {
          var extEnums: Record<string, Array<ClaimValue>> = (await COSMOS_API.POST(
            {
              id: appData.id,
              orgId: appOrgTarget
            },
            POST_ENDPOINT.GetApplicationExternalEnumerations,
            getToken()
          )).respBody;
          setAppExternalEnums({
            ...appExternalEnums,
            [appData.id]: makeExternalEnumRecord(extEnums)
          })
        }
      }
      if (!isGlobalApp) {
      if (
        newUser.organizations[organizationToEdit] !== undefined &&
        newUser.organizations[organizationToEdit][selected.value] !== undefined
      ) {
          /* if (applicationData !== undefined && applicationData.name === complexApps.learningCompass)
                      brandsNeeded = true; */
        newApps[selected.value] = {
          ...newUser.organizations[organizationToEdit][selected.value],
        };
      } else {
        newApps[selected.value] = {
          userProvisionStatus: 'Approved',
        };
      }
      } else {
        if (
          newUser.applications[selected.value] !== undefined
        ) {
          newGlobalApps[selected.value] = {
            ...newUser.applications[selected.value]
          };
        } else {
          newGlobalApps[selected.value] = {
            userProvisionStatus: 'Approved'
          }
        }
      }
      let appValidUserTypes = validUserTypeAppInfo[selected.value];
      if (appValidUserTypes != null) {
        if (validUserTypesInfo == null) {
          validUserTypesInfo = [...appValidUserTypes];
        } else {
          validUserTypesInfo = validUserTypesInfo.filter(userType => appValidUserTypes!.includes(userType));
        }
    }
    }

    setAppClaims(newAppClaims);
    setValidUserTypes(validUserTypesInfo);
    setValidUserTypesByApp({ ...validUserTypeAppInfo });

    if ((validUserTypesInfo != null) && ((newUser.userType == null) || (!validUserTypesInfo.includes(newUser.userType)))) {
      dispatch({
        type: UserUpdateAction.USERTYPE,
        payload: {
          data: validUserTypesInfo.length > 0 ? validUserTypesInfo[0] : null
        }
      });
    } else if (validUserTypesInfo == null) {
      dispatch({
        type: UserUpdateAction.USERTYPE,
        payload: {
          data: null
        }
      });
    }

    dispatch({
      type: UserUpdateAction.SINGLEORG,
      payload: { field: organizationToEdit, data: { orgData: { ...newApps }, globalAppData: { ...newGlobalApps } } },
    });
  };

  const createUser = async (e: any) => {
    e.preventDefault();
    triggerPopUp('Processing please wait.');
    document.getElementById('add-user-processing')?.classList.add('loading');
    const emailRegex = /[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$/;

    var userToSubmit = structuredClone(newUser);

    if (userToSubmit.country === '') {
      userToSubmit.country = 'us';
    }
    if (userToSubmit.defaultLanguage === '')
      userToSubmit.defaultLanguage = 'en-us';

    if (
      userToSubmit.firstName === '' ||
      userToSubmit.lastName === '' ||
      userToSubmit.email === '' ||
      userToSubmit.country === '' ||
      newUser.defaultLanguage === ''
    ) {
      enqueueSnackbar(
        'A required field is blank! Please ensure email, first name, and last name are provided',
        {variant: 'error'}
      );
      return;
    }
    if (userToSubmit.email.toLowerCase().match(emailRegex) === null) {
      enqueueSnackbar('Invalid email address format.',
      {variant: 'error'});
      return;
    }

    const resp = await COSMOS_API.POST(
      { users: [userToSubmit], notifyUsersThroughEmail: setupInstructions, defaultApplication: defaultApp !== undefined ? defaultApp.value : selectedApps[0].value },
      POST_ENDPOINT.BatchAddUser,
      getToken()
    );

    if (
      resp.ok && resp.respBody !== undefined && resp.respBody !== null && resp.respBody.length > 0 && resp.respBody[0].success
    ) {
      enqueueSnackbar('User successfully created!', {variant: 'success'});
      setValidUserTypes(null);
      let baseOrganization = adminCredentials.organizationList?.length > 1 ? "" : adminCredentials.organizationId;
      if (baseOrganization) {
        dispatch({
          type: UserUpdateAction.REPLACEUSER,
          payload: {
            data: {
              ...init_newUserDataObj()
            },
          },
        });
      } else {
      dispatch({
        type: UserUpdateAction.REPLACEUSER,
        payload: {
          data: {
            ...init_newUserDataObj(),
              organizations: { [baseOrganization]: { [ORG_SHARED_DATA]: {} } },
          },
        },
      });
      }
      setOrganizationToEdit(baseOrganization);
      setSelectedApps([]);
      closePopup();
    } else if(
      resp.ok && resp.respBody !== undefined && resp.respBody !== null && resp.respBody.length > 0 && resp.respBody[0].success === false
    ){
      let respMsg =
        resp.respBody[0].errorMessage !== undefined ? `, ${resp.respBody[0].errorMessage}` : '';
      triggerPopUp(`Error creating user ${respMsg}`);
    } else {
      let respMsg =
        resp.respBody.errorMessage !== undefined ? `, ${resp.respBody.errorMessage}` : '';
      triggerPopUp(`Error creating user ${respMsg}`);
    }
  };

  const triggerPopUp = (msg_content: string) => {
    setMsg(msg_content);
    setShowMsg(true);
  };

  const closePopup = () => {
    setMsg('');
    setShowMsg(false);
  }

  const submitDisabled = () => {
    return newUser.firstName === '' ||
      newUser.lastName === '' ||
      isNullUndefEmpty(organizationToEdit) ||
      ((validUserTypes != null) && ((newUser.userType == null) || !validUserTypes.includes(newUser.userType))) ||
      ((globalLevelClaims != null) && globalLevelClaims.filter(gClaim => gClaim.required).some(gClaim => gClaim.isBaseClaim ? isNullUndefEmpty((newUser as any)[gClaim.id]) : isNullUndefEmpty(newUser.global[gClaim.id]))) ||
      ((orgLevelClaims != null) && orgLevelClaims.filter(oClaim => oClaim.required).some(oClaim => isNullUndefEmpty(newUser.organizations?.[organizationToEdit]?.sharedData?.[oClaim.id]))) ||
      ((appClaims != null) && Object.keys(appClaims).some(appIdx => (appClaims[appIdx]) && (appClaims[appIdx].length > 0) && (appClaims[appIdx].some(aClaim => isNullUndefEmpty(adminCredentials.allApplications[appIdx].isGlobal ? newUser.applications?.[appIdx]?.[aClaim.id] : newUser.organizations?.[organizationToEdit]?.[appIdx]?.[aClaim.id])))));
  }

  return organizations.length > 0 ? (
    <div className="full-section-container">
      {showMsg ? (
        <PopUp msg={msg} setShowMsg={setShowMsg} showMsg={showMsg} />
      ) : null}
      <Container>
        <Typography variant="h3"> Add New User </Typography>
        <Grid container direction="column" spacing={4}>
          <Grid item container direction="row" spacing={4}>
            <Grid item md={6}>
              <Typography variant="subtitle1"> Instructions </Typography>
              <ul>
                <li>
                  Select one or more applications to give the user access to.
                </li>
                <li>
                  Specify any specific permissions for each of these
                  applications.
                </li>
                <li> Fill out the user information below. </li>
              </ul>
            </Grid>
            <Grid item md={6} container direction='column' spacing={2}>
            <Grid item
            sx={{display: (adminCredentials.isSuperAdmin || (adminCredentials.isOrganizationAdmin && adminCredentials.multiOrg)) ? 'block': 'none'}}>
                <Autocomplete
                  id="org"
                  fullWidth
                  value={organizations.find(org => org.id === organizationToEdit) ?? null}
                  isOptionEqualToValue={(
                    option: {id: string, name: string},
                    value: {id: string, name: string}
                  ) => option.id === value.id}
                  options={organizations}
                  getOptionLabel={(option:  {id: string, name: string}) => option.name}
                  renderInput={(params) => (
                    <TextField {...params} label={`Organization (Required)`} />
                  )}
                  onChange={(
                    _: any,
                    value: {id: string, name: string} | null
                  ) => {
                    if (value === null) return;
                    setOrganizationToEdit(value.id);
                  }}
                />
              </Grid>
              <Grid item>
                <Autocomplete
                  id="applications"
                  fullWidth
                  multiple
                  value={selectedApps}
                  isOptionEqualToValue={(
                    option: OptionsType,
                    value: OptionsType
                  ) => option.value === value.value}
                  options={applications}
                  getOptionLabel={(option: OptionsType) => option.label}
                  renderInput={(params) => (
                    <TextField {...params} label={`Application (Required)`} />
                  )}
                  onChange={(
                    _: any,
                    value: OptionsType | OptionsType[] | null
                  ) => {
                    if (value === null) return;
                    if (!Array.isArray(value)) value = [value];
                    handleChange(value);
                  }}
                />
              </Grid>
              {
                selectedApps.length > 1 ? 
                <Grid item>
                <Autocomplete
                  id="defaultApplication"
                  fullWidth
                  disableClearable
                  value={defaultApp}
                  isOptionEqualToValue={(
                    option: OptionsType,
                    value: OptionsType
                  ) => option.value === value.value}
                  options={selectedApps}
                  getOptionLabel={(option: OptionsType) => option.label}
                  renderInput={(params) => (
                    <TextField {...params} label={`Default App`} />
                  )}
                  onChange={(
                    _: any,
                    value: OptionsType | null
                  ) => {
                    if (value === null) return;
                    setDefaultApp(value);
                  }}
                />
              </Grid> : <></>
              }
            </Grid>
          </Grid>
          <Grid item container direction="row" spacing={4}>
            <Grid item container>
              <Grid item>
                <Typography variant="h6"> User Info </Typography>
              </Grid>
              <Grid item container spacing={2}>
                <Grid item md={6}>
                  <Stack spacing={2}>
                    <TextField
                      id="email"
                      label="Email Address (Required)"
                      value={newUser.email}
                      onChange={(e) =>
                        dispatch({
                          type: UserUpdateAction.TOPLEVEL,
                          payload: {
                            field: e.target.id,
                            data: e.target.value,
                          },
                        })
                      }
                    />
                    <TextField
                      id="firstName"
                      label="First Name (Required)"
                      value={newUser.firstName}
                      onChange={(e) =>
                        dispatch({
                          type: UserUpdateAction.TOPLEVEL,
                          payload: {
                            field: e.target.id,
                            data: e.target.value,
                          },
                        })
                      }
                    />

                    <TextField
                      id="lastName"
                      label="Last Name (Required)"
                      value={newUser.lastName}
                      onChange={(e) =>
                        dispatch({
                          type: UserUpdateAction.TOPLEVEL,
                          payload: {
                            field: e.target.id,
                            data: e.target.value,
                          },
                        })
                      }
                    />

                    {(globalLevelClaims && (globalLevelClaims.length > 0)) && (
                      globalLevelClaims.filter(glClaim => claimEnabledInContext(glClaim, claimsListToSchema(globalLevelClaims.filter(ggClaim => ggClaim.isBaseClaim == glClaim.isBaseClaim)), glClaim.possibleValues, glClaim.isBaseClaim ? (newUser as any) : newUser.global)).map((claim) => {
                        return (
                          <ApplicationClaimField
                            key={`global-claim-${claim.id}`}
                            claim={claim}
                            disabled={false}
                            claimValue={claim.isBaseClaim ? ((newUser as any)[claim.id]) : newUser.global?.[claim.id]}
                            setClaimValue={(aid, cid, cvalue) => {
                              dispatch({
                                type: claim.isBaseClaim ? UserUpdateAction.TOPLEVEL : UserUpdateAction.GLOBAL,
                                payload: {
                                  field: cid,
                                  data: cvalue
                                }
                              });
                            }}
                            appId={""}
                            appExternalEnums={appExternalEnums}
                            claimContext={claim.isBaseClaim ? (newUser as any) : newUser.global}
                            claimSchema={claimsListToSchema(globalLevelClaims.filter(ggClaim => ggClaim.isBaseClaim == claim.isBaseClaim))}
                          />
                        )
                      })
                    )}

                    {newUser.organizations[organizationToEdit] !== undefined ? (
                      orgLevelClaims.map((claim) => {
                        return (
                          <TextField
                            id={claim.id}
                            key={claim.id}
                            label={claim.id}
                            value={
                              newUser.organizations[organizationToEdit][
                                'sharedData'
                              ][claim.id] ?? ''
                            }
                            onChange={(e) =>
                              dispatch({
                                type: UserUpdateAction.SINGLEAPPCLAIMS,
                                payload: {
                                  field: organizationToEdit,
                                  data: {
                                    appId: 'sharedData',
                                    appClaim: claim.id,
                                    appValue: e.target.value,
                                  },
                                },
                              })
                            }
                          />
                        );
                      })
                    ) : (
                      <></>
                    )}
                  </Stack>
                </Grid>
                <Grid item md={6}>
                  <Stack spacing={2}>
                    {(validUserTypes != null) && (validUserTypes.length > 0) && (
                      <TextField
                        id="selectUserType"
                        select
                        label="User Type"
                        disabled={false}
                        value={newUser.userType ?? validUserTypes[0]}
                        onChange={(e) => {
                          dispatch({
                            type: UserUpdateAction.USERTYPE,
                            payload: {
                              data: e.target.value
                            }
                          });
                        }}
                      >
                        {validUserTypes.map((userType) => (
                          <MenuItem key={`userType-${userType}`} value={userType}>
                            {userType}
                          </MenuItem>
                        ))}
                      </TextField>
                    )}
                    {(validUserTypes != null) && (validUserTypes.length == 0) && (
                      <Alert icon={<ErrorIcon fontSize="inherit" />} variant='filled' severity='error'>
                        No user type is valid for all selected applications.
                      </Alert>
                    )}
                    {Object.keys(appClaims).map((appKey) => {
                      if (
                        Object.keys(appClaims[appKey]).length === 0 ||
                        (adminCredentials.allApplications[appKey].isGlobal && 
                          newUser.applications[appKey] === undefined
                        ) ||
                        ((!adminCredentials.allApplications[appKey].isGlobal) && (
                        newUser.organizations[organizationToEdit] === undefined ||
                        newUser.organizations[organizationToEdit][appKey] ===
                          undefined
                        ))
                      )
                        return [];
                      return appClaims[appKey].filter(ac => claimEnabledInContext(ac, claimsListToSchema(appClaims[appKey]), ac.externalEnumeration ? (appExternalEnums?.[appKey]?.[ac.id]?.values ?? []) : ac.possibleValues, adminCredentials.allApplications[appKey].isGlobal ? newUser.applications[appKey] : newUser.organizations[organizationToEdit][appKey])).map((claim) => {
                        return (
                          <ApplicationClaimField
                            claim={claim}
                            disabled={false}
                            claimValue={adminCredentials.allApplications[appKey].isGlobal ?
                              newUser.applications[appKey][claim.id]
                              : newUser.organizations[organizationToEdit][appKey][claim.id]}
                            setClaimValue={(aid, cid, cvalue) => {
                              dispatch({
                                type: UserUpdateAction.SINGLEAPPCLAIMS,
                                payload: {
                                  field: organizationToEdit,
                                  data: {
                                    isGlobal: adminCredentials.allApplications[appKey].isGlobal,
                                    appId: appKey,
                                    appClaim: claim.id,
                                    appValue: cvalue,
                                  },
                                },
                              })
                            }}
                            appId={appKey}
                            appExternalEnums={appExternalEnums}
                            claimContext={adminCredentials.allApplications[appKey].isGlobal ? newUser.applications[appKey] : newUser.organizations[organizationToEdit][appKey]}
                            claimSchema={claimsListToSchema(appClaims[appKey])}
                          />
                        );
                      });
                    })}
                  </Stack>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
          <Grid item container direction='row' spacing={12}>
              {
                //@ts-ignore
              AppEnvSetting.hideInviteEmail !== true && <Grid item md={6}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={setupInstructions}
                      onChange={(e: any) => {
                        if (e) setSetupInstructions(e.target.checked);
                      }}
                    />
                  }
                  label="Email setup instructions to user?"
                />
              </Grid>}
            <Grid item md={6} sx={{display: 'flex', justifyContent: 'flex-end'}}>
              <Button
                disabled={submitDisabled()}
                onClick={createUser}
              >
                Add User
              </Button>
            </Grid>
          </Grid>
        </Grid>
      </Container>
    </div>
  ) : null;
};

export default AddUser;
